import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAuthContext } from "hooks/useAuthContext";
import { useProjectContext } from "hooks/useProjectContext";
import { Box, CircularProgress } from "@mui/material";
import { API_URL } from "../../../../config";
import DeleteHomePageProjectPopup from "../components/deleteHomePagePopUp";
import { toast } from "sonner";
import AddHomePageProjectImage from "../components/addHomePageProjectImagePopup";

export default function useData() {
  const { user } = useAuthContext();
  const { projects, dispatch } = useProjectContext();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);

  // Fetch chosen homepage projects from the server
  const fetchHomepageProjects = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_URL}/projects/gethomepageprojects`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${user.token}`,
        },
      });

      if (!response.ok) {
        toast.error("Failed to load projects. Please try again.");
        return;
      }
      const suggestedProjects = await response.json();
      dispatch({ type: "GET_PROJECTS", payload: suggestedProjects });
    } catch (error) {
      toast.error("Failed to load projects. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [user, dispatch]);

  useEffect(() => {
    if (user) fetchHomepageProjects();
  }, [user, fetchHomepageProjects]);

  const columns = useMemo(
    () => [
      { Header: "Photo", accessor: "photo", width: "10%" },
      { Header: "Title", accessor: "title", width: "40%" },
      { Header: "Description", accessor: "description", width: "50%" },
      { Header: "Delete", accessor: "delete", align: "center" },
    ],
    []
  );

  const rows = useMemo(
    () =>
      projects
        ? projects.map((project) => ({
            photo: (
              <AddHomePageProjectImage
                project={project}
                fetchHomepageProjects={fetchHomepageProjects}
              />
            ),
            title: project.projectTitle,
            description: project.projectDescription.slice(0, 50) + "....",
            delete: (
              <Box>
                <DeleteHomePageProjectPopup
                  homepageproject={project}
                  fetchHomepageProjects={fetchHomepageProjects}
                />
              </Box>
            ),
          }))
        : [],
    [projects, fetchHomepageProjects]
  );

  if (loading) {
    return {
      columns,
      rows: [
        {
          photo: <CircularProgress size={20} />,
          title: <CircularProgress size={20} />,
          description: <CircularProgress size={20} />,
          delete: null,
        },
      ],
    };
  }
  return { columns, rows, fetchHomepageProjects };
}
